import { InvalidApiResponseError, InvalidOriginalImageSizeLimitError } from './stickerizer.error';
import { ImageUtil } from '@modules/Vector/S/Function/util.image';

export class StickerizerValidator {
  /**
   * Generic validator for API responses with specific shape requirements
   * @param response - The API response to validate
   * @param operationName - The name of the operation for error reporting
   * @param validator - Function that validates the response shape
   * @throws InvalidApiResponseError if validation fails
   */
  private validateResponse<T extends Record<string, unknown>>(
    response: unknown,
    operationName: string,
    validator: (obj: Record<string, unknown>) => obj is T,
  ): asserts response is T {
    if (typeof response !== 'object' || response === null) {
      throw new InvalidApiResponseError(operationName, { response });
    }

    const responseObj = response as Record<string, unknown>;
    if (!validator(responseObj)) {
      throw new InvalidApiResponseError(operationName, { response });
    }
  }

  /**
   * Type guard for image response validation
   */
  private isImageResponse(
    obj: Record<string, unknown>,
  ): obj is { url: string; width: number; height: number } {
    return typeof obj.url === 'string' && typeof obj.width === 'number' && typeof obj.height === 'number';
  }

  /**
   * Type guard for outline response validation
   */
  private isOutlineResponse(
    obj: Record<string, unknown>,
  ): obj is { svg: string; width: number; height: number } {
    return typeof obj.svg === 'string' && typeof obj.width === 'number' && typeof obj.height === 'number';
  }

  /**
   * Validates API response for image operations (trim, binarize)
   * @param response - The API response to validate
   * @param operationName - The name of the operation for error reporting
   * @throws InvalidApiResponseError if validation fails
   */
  validateImageResponse(
    response: unknown,
    operationName: string,
  ): asserts response is { url: string; width: number; height: number } {
    this.validateResponse(response, operationName, this.isImageResponse);
  }

  /**
   * Validates API response for outline vectorize operation
   * @param response - The API response to validate
   * @param operationName - The name of the operation for error reporting
   * @throws InvalidApiResponseError if validation fails
   */
  validateOutlineResponse(
    response: unknown,
    operationName: string,
  ): asserts response is { svg: string; width: number; height: number } {
    this.validateResponse(response, operationName, this.isOutlineResponse);
  }

  /**
   * Validate image file size before processing
   * @param imageUrl - The image URL to validate
   * @param maxSizeMB - Maximum allowed file size in MB
   * @throws InvalidOriginalImageSizeLimitError if image is too large
   */
  async validateImageSize(imageUrl: string, maxSizeMB: number): Promise<void> {
    const fileSize = await ImageUtil.getImageFileSize(imageUrl);

    if (fileSize !== null) {
      // Convert bytes to MB for comparison
      const fileSizeMB = fileSize / (1024 * 1024);

      if (fileSizeMB > maxSizeMB) {
        throw new InvalidOriginalImageSizeLimitError({
          fileSize,
          fileSizeMB: Math.round(fileSizeMB * 100) / 100,
          maxSizeMB,
          imageUrl,
        });
      }
    }
  }
}
